import React, { useEffect, useState } from 'react';
import { Card, List, Input, Button, message, Space, Popconfirm, Typography, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { globalSettingsApi } from '../../services/api';

const { Title, Paragraph } = Typography;

const GlobalSettings: React.FC = () => {
  const [options, setOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await globalSettingsApi.getSetting('filter_rule_options');
      setOptions(data.options || []);
    } catch (error: any) {
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!newOption.trim()) {
      message.warning('Please enter an option value');
      return;
    }

    if (options.some(opt => opt.toLowerCase() === newOption.trim().toLowerCase())) {
      message.warning('This option already exists');
      return;
    }

    setOptions([...options, newOption.trim()]);
    setNewOption('');
  };

  const handleRemove = (option: string) => {
    setOptions(options.filter(o => o !== option));
  };

  const handleSave = async () => {
    if (options.length === 0) {
      message.warning('You must have at least one filter option');
      return;
    }

    setSaving(true);
    try {
      await globalSettingsApi.updateSetting('filter_rule_options', { options });
      message.success('Global filter settings saved successfully');
    } catch (error: any) {
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Title level={2}>Global Filter Settings</Title>

      <Alert
        type="warning"
        message="Master Filter Configuration"
        description={
          <>
            <Paragraph style={{ marginBottom: 8 }}>
              These options define the <strong>MASTER FILTER</strong> for the entire system:
            </Paragraph>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li><strong>Admins</strong> will ONLY see messages containing ANY of these keywords in FROM or CONTENT fields</li>
              <li><strong>Users</strong> can select from these options to create their personal filter rules</li>
              <li><strong>SuperAdmins</strong> see ALL messages regardless of filters</li>
              <li>Messages NOT matching any option are <strong>hidden from everyone except SuperAdmins</strong></li>
            </ul>
          </>
        }
        style={{ marginBottom: 24 }}
      />

      <Card
        title="Filter Rule Options"
        extra={
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="Enter new filter option (e.g., Google, Facebook)"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onPressEnter={handleAdd}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add Option
              </Button>
            </Space.Compact>
          </div>

          <List
            loading={loading}
            bordered
            dataSource={options}
            locale={{ emptyText: 'No filter options configured. Add at least one option above.' }}
            renderItem={(option) => (
              <List.Item
                actions={[
                  <Popconfirm
                    key="delete"
                    title="Remove this option?"
                    description="Users with this filter rule will lose access to those messages."
                    onConfirm={() => handleRemove(option)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" danger icon={<DeleteOutlined />}>
                      Remove
                    </Button>
                  </Popconfirm>
                ]}
              >
                <Typography.Text strong>{option}</Typography.Text>
              </List.Item>
            )}
          />
        </Space>
      </Card>
    </div>
  );
};

export default GlobalSettings;
